import {
  ChecksumAddress,
  ProtocolInstance,
  ProviderPriority,
  getProtocolInstanceChainId,
} from "@silo/types";
import { isSiloReady } from "./isSiloReady";
import { pipe } from "fp-ts/lib/function";
import { array, option } from "fp-ts";
import { createClientProvider } from "@silo/utils";

const collisionPriorityOrder: ProtocolInstance[] = [
  "arbitrum",
  "optimism",
  "base",
  "lrtbase",
  "mainnetLegacy",
  "mainnetGrand",
  "mainnetLlama",
  "anvil",
  "local",
];

type IsValidSiloResult =
  | { valid: false; protocolInstance: null }
  | {
      valid: true;
      protocolInstance: ProtocolInstance;
    };

// TODO: includes all protocol instances if there's a collision
type IsValidSilo = (params: {
  address: ChecksumAddress;
  protocolInstances: ProtocolInstance[];
  providerPriority: ProviderPriority;
}) => Promise<IsValidSiloResult>;

/**
 * Checks to see if an address is a valid silo for the provided protocol instances.
 *
 * A silo is valid if it meets the following conditions:
 * - `SiloRepository.isSilo` is `true`
 * - `PriceProvidersRepository.providersReadyForAsset` is `true` for the silo asset (we assume it's true for bridge assets)
 */
export const isValidSilo: IsValidSilo = async ({
  address,
  protocolInstances,
  providerPriority,
}) => {
  const results: Result[] = await Promise.all(
    protocolInstances.map(async (protocolInstance): Promise<Result> => {
      const chainId = getProtocolInstanceChainId(protocolInstance);
      const clientProvider = createClientProvider(chainId, providerPriority);

      return {
        valid: await isSiloReady(clientProvider, protocolInstance, address),
        protocolInstance,
      };
    })
  );

  const sorted = results.sort(
    (a, b) =>
      collisionPriorityOrder.indexOf(a.protocolInstance) -
      collisionPriorityOrder.indexOf(b.protocolInstance)
  );

  return pipe(
    sorted,
    array.filter(({ valid }) => valid),
    array.head,
    option.fold(
      (): IsValidSiloResult => ({ valid: false, protocolInstance: null }),
      ({ protocolInstance }): IsValidSiloResult => ({
        valid: true,
        protocolInstance,
      })
    )
  );
};

interface Result {
  valid: boolean;
  protocolInstance: ProtocolInstance;
}
